import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Logo } from "@/components/app/Logo";
import PodcastSection from "@/components/app/PodcastSection";
import FreeAccessPopup from "@/components/app/FreeAccessPopup";
import {
  Check, Sparkles, Mail, Users, ShieldCheck, CalendarDays,
  HeartPulse, BookOpen, Trophy, Send, Star, Quote, Instagram,
} from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { icon: CalendarDays, title: "Calendar & weekly journal", body: "Log grooming, training, shows, and notes — see the whole week, month, or year at a glance." },
  { icon: HeartPulse, title: "Health & vet records", body: "Weight, food, supplements, vaccinations, and vet files for every dog you track." },
  { icon: Sparkles, title: "Daily / weekly / monthly prompts", body: "Emails tailored to puppy, pet, show, sport, or senior dogs — never forget a session. Pick the time, pick the days, get a nudge in your inbox so you actually do it." },
  { icon: Users, title: "Multi-user sharing", body: "Co-owners, handlers, and family stay in sync — view-only or edit access." },
  { icon: Trophy, title: "Show plans + results", body: "Plan upcoming shows with budgets, log entries and results, watch the year build." },
  { icon: BookOpen, title: "Continual Improvement", body: "Upload ebooks from Leading Edge Dog Show Academy and other providers to keep educational materials in one place." },
  { icon: Star, title: "January YDD Summit", body: "Attend the summit to get the most out of Your Dog Days and set up plans for success for the year." },
  { icon: ShieldCheck, title: "Year-in-review", body: "Cumulative stats every January — points earned, titles, shows attended, training mix." },
];

const GALLERY = [
  { src: "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/of3k11ld_Alfred%20second%20BG%20Classic%202023.jpg", caption: "Show day" },
  { src: "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/kg4wx7w6_nimbus%20pink.JPG", caption: "Service dog" },
  { src: "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/xivr8qdz_Mary%20stacked%20at%20Jody%27s.jpg", caption: "Stacked & ready" },
  { src: "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/cmvpg8op_A37I6920-sm.jpg", caption: "Lure Coursing" },
  { src: "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/oqfzcvfr_link%20angel%20blue%20sweater.jpg", caption: "Puppy love" },
  { src: "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/c1x7xz59_sa-A37I9225.jpg", caption: "Heel work" },
  { src: "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/0c4txsls_2021%20litter%20in%20the%20sun%20and%20grass.jpg", caption: "Puppy days" },
  { src: "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/jcaei1pn_A37I6872-sm.jpg", caption: "Fun Runs" },
];

const HERO_PHOTO = "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/x9ktdk8l_Tiina%20with%20Dino.JPG";

const TESTIMONIALS = [
  { quote: "I wanted to thank you for having such a great resource for the dog community and having so much information!" },
  { quote: "I finally bit the bullet and subscribed to Leading Edge Dog Show Academy. After listening to the podcast episode with Allison, I feel like it’s a must for showing as well as grooming." },
  { quote: "Really enjoyed…especially hearing your puppies in the background. Always informative and great guests." },
];

function Section({ id, children, className = "" }) {
  return <section id={id} className={`max-w-6xl mx-auto px-6 sm:px-10 ${className}`}>{children}</section>;
}

export default function LandingPage() {
  const { user, isLoading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // "Join the Community" — lightweight newsletter-only signup (no password,
  // no dog). Backed by POST /api/newsletter/subscribe.
  const [community, setCommunity] = useState({ name: "", email: "" });
  const [communityState, setCommunityState] = useState("idle"); // idle | sending | subscribed | already

  // If a logged-in user lands here, fast-forward to their dashboard.
  useEffect(() => {
    if (!isLoading && user) nav("/welcome", { replace: true });
  }, [isLoading, user, nav]);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Fill out every field, please");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Message sent");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const submitCommunity = async (e) => {
    e.preventDefault();
    if (!community.email.trim()) {
      toast.error("Enter an email");
      return;
    }
    setCommunityState("sending");
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: community.email.trim().toLowerCase(),
        name: community.name.trim(),
      });
      setCommunityState(data?.already ? "already" : "subscribed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe");
      setCommunityState("idle");
    }
  };

  return (
    <div className="bg-background text-foreground min-h-screen" data-testid="landing-page">
      <FreeAccessPopup />
      {/* Nav */}
      <header className="border-b border-border bg-card/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <Logo size={56} textClassName="text-xl" />
          <nav className="flex items-center gap-1 sm:gap-3">
            <a href="#features" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2">Features</a>
            <a href="#podcast" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2">Podcast</a>
            <a href="#pricing" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2">Pricing</a>
            <a href="#community" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2" data-testid="nav-community">Community</a>
            <a href="#contact" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2">Contact</a>
            <Link to="/login" data-testid="landing-signin-link">
              <Button variant="ghost" size="sm">Sign in</Button>
            </Link>
            <Link to="/register" data-testid="landing-register-link">
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">Get started</Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <Section className="pt-16 pb-14 sm:pt-24 sm:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#CC9C33] font-semibold" data-testid="hero-eyebrow">
              Built for handlers · Loved by pet parents
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.02] mt-3 text-primary" data-testid="hero-headline">
              Every walk, weigh-in, and win — captured in one place.
            </h1>
            <p className="text-base sm:text-lg text-foreground/80 mt-6 max-w-prose" data-testid="hero-subhead">
              Your Dog Days is the journal + planner used by <strong>show handlers</strong>, <strong>trainers</strong>, <strong>breeders</strong>, and <strong>pet parents</strong> — from your puppy&apos;s first socialization checklist all the way to championship paperwork and senior years.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/register" data-testid="hero-cta-register">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Sparkles size={16} className="mr-1.5" /> Get started — it&apos;s free
                </Button>
              </Link>
              <a href="#pricing" className="text-sm font-medium underline underline-offset-4">See pricing →</a>
            </div>
            <ul className="mt-8 space-y-1.5 text-sm text-foreground/75">
              <li className="flex items-center gap-2"><Check size={14} className="text-primary shrink-0" /> Free plan, no credit card required</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-primary shrink-0" /> Upgrade anytime · cancel anytime</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-primary shrink-0" /> Your data stays put if Pro ends</li>
            </ul>

            {/* Founder credibility strip — high-signal trust line placed
                right under the trust bullets so it's visible above the fold.
                Real bona fides do more than any social-proof stat could. */}
            <div className="mt-8 flex items-start gap-3 rounded-lg border-l-4 border-[#CC9C33] bg-secondary/40 px-4 py-3" data-testid="hero-founder-note">
              <p className="text-sm text-foreground/85 leading-relaxed">
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#CC9C33] font-bold block mb-1">The founder</span>
                Built by a pet parent turned conservation breeder who shows in <strong>Bred-By-Exhibitor</strong> and <strong>Amateur Owner Handler</strong> classes — every feature comes from real experience in a show ring, whelping box, or vet visit.
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-secondary rounded-2xl -z-10 rotate-2" />
            <Card className="border-2 border-primary shadow-xl rounded-xl overflow-hidden">
              <CardContent className="p-0 bg-card">
                <img
                  src={HERO_PHOTO}
                  alt="A handler high-fives a standard poodle under blooming cherry blossoms"
                  className="w-full h-[420px] sm:h-[480px] object-cover"
                  loading="eager"
                  data-testid="hero-photo"
                />
              </CardContent>
            </Card>
            <div className="hidden sm:flex absolute -bottom-6 -left-6 bg-card border-2 border-primary rounded-lg shadow-lg px-4 py-3 items-center gap-3">
              <Logo size={36} variant="mark" showText={false} />
              <div>
                <p className="font-display text-sm font-extrabold tracking-tight text-primary leading-none">Your Dog Days</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">Plan. Track. Succeed.</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Who it's for — 4-persona value strip. Renders directly under the
          hero so cold-arriving visitors immediately see themselves reflected
          before scrolling. Each card is anchor-linked to the deeper feature
          section that speaks to that persona later on the page. */}
      <Section className="pb-16 sm:pb-24" data-testid="landing-personas">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-semibold">Who it&apos;s for</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-2 text-foreground">
            One app. Four kinds of dog people.
          </h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              key: "handler",
              overline: "For show handlers",
              title: "Track titles, wins, and paperwork.",
              body: "Every show entry, judge, class, and championship point in one timeline. Grooming logs, health testing, and pedigree links keep working dogs ready for the ring.",
              anchor: "#features",
            },
            {
              key: "trainer",
              overline: "For trainers",
              title: "Log sessions, share progress.",
              body: "Structured session notes per dog — cues learned, behaviors emerging, weight and nutrition trends. Give clients a private view so owners stay engaged between lessons.",
              anchor: "#features",
            },
            {
              key: "breeder",
              overline: "For breeders",
              title: "From heat cycle to buyer handoff.",
              body: "Heat cycle tracking, gestation calendars, whelping notes, and per-puppy records. Send new puppy owners a private invite so they inherit weights, vet visits, and photos from day one.",
              anchor: "#features",
            },
            {
              key: "pet-parent",
              overline: "For pet parents",
              title: "The whole life, kept together.",
              body: "Vet appointments, vaccinations, weight charts, food notes, grooming, and a photo journal. Puppy or senior — never lose the dates that matter, and always know what to ask the vet next.",
              anchor: "#features",
            },
          ].map((p) => (
            <a
              key={p.key}
              href={p.anchor}
              data-testid={`persona-card-${p.key}`}
              className="group block"
            >
              <Card className="border-border shadow-none rounded-lg h-full hover:border-primary/40 hover:shadow-md transition-all">
                <CardContent className="p-6 flex flex-col h-full">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#CC9C33] font-bold">
                    {p.overline}
                  </p>
                  <h3 className="font-display text-xl font-extrabold tracking-tight text-primary mt-2 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-sm text-foreground/75 mt-3 leading-relaxed flex-1">
                    {p.body}
                  </p>
                  <p className="text-xs font-semibold text-primary mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    See how →
                  </p>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </Section>

      {/* Photo gallery / social proof */}
      <Section className="pb-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="landing-gallery">
          {GALLERY.map((g, i) => (
            <figure
              key={g.src}
              className={`relative overflow-hidden rounded-lg border border-border group ${i === 0 ? "col-span-2 sm:col-span-1" : ""}`}
            >
              <img
                src={g.src}
                alt={g.caption}
                className="w-full h-32 sm:h-40 object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                loading="lazy"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent text-[11px] uppercase tracking-[0.22em] text-white px-3 py-2">
                {g.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* Features */}
      <Section id="features" className="py-20 sm:py-28">
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">What&apos;s inside</p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter mt-2">
            Your Dog Days has all the planning tools you need for success.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f) => (
            <Card key={f.title} className="border-border shadow-none rounded-lg hover:-translate-y-0.5 hover:border-primary transition-all" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <CardContent className="p-6">
                <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center mb-3">
                  <f.icon size={18} />
                </div>
                <p className="font-medium">{f.title}</p>
                <p className="text-sm text-muted-foreground mt-1.5">{f.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Testimonials */}
      <Section className="py-16 sm:py-20 bg-secondary/30">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Loved by</p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter mt-2">
            Handlers, breeders, and pet parents.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="testimonials">
          {TESTIMONIALS.map((t, i) => (
            <Card key={t.quote.slice(0, 40)} className="border-border shadow-none rounded-lg bg-card">
              <CardContent className="p-6 sm:p-7 h-full flex flex-col">
                <Quote size={22} className="text-primary/60 shrink-0" />
                <p className="text-foreground/85 leading-relaxed mt-3 flex-1">&ldquo;{t.quote}&rdquo;</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      <PodcastSection />
      <Section id="pricing" className="py-20 sm:py-28">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Pricing</p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter mt-2">
            Simple pricing that scales with your kennel.
          </h2>
          <p className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-primary mt-3" data-testid="pricing-free-until">
            Free until 2027.
          </p>
          <p className="text-sm text-muted-foreground mt-3">
            Start free with one dog. Pay only when you add more. <span className="text-foreground/70">Yearly plans save ~17%.</span>
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-stretch">
          {[
            {
              name: "Free", price: "$0", period: "/forever", testid: "pricing-free",
              cta: "Start free", action: { to: "/register" },
              perks: [
                "1 active dog",
                "Profile, Health & Vet, Show Plans",
                "Read-only Calendar & Journal",
                "No email reminders / sharing",
              ],
            },
            {
              name: "Starter", price: "$10", period: "/mo", subPrice: "or $100/yr",
              testid: "pricing-starter", cta: "Get Starter", action: { to: "/register" },
              perks: [
                "Up to 5 dogs",
                "Full Calendar & Journal",
                "Daily / weekly / monthly prompts",
                "Email reminders + sharing",
              ],
            },
            {
              name: "Pro", price: "$15", period: "/mo", subPrice: "or $150/yr",
              testid: "pricing-pro", cta: "Get Pro", action: { to: "/register" }, highlight: true,
              perks: [
                "Up to 10 dogs",
                "Everything in Starter",
                "Live Webinars",
                "Yearly Conference (January)",
              ],
            },
            {
              name: "Enterprise", price: "Let's talk", period: "", testid: "pricing-enterprise",
              cta: "Contact us",
              action: { href: "mailto:hello@yourdogdays.com?subject=Enterprise%20pricing%20enquiry" },
              perks: [
                "11+ dogs",
                "Breeders, clubs, academies",
                "Custom group pricing",
                "Bulk puppy-profile transfers",
              ],
            },
          ].map((p) => (
            <Card key={p.name} className={`shadow-none rounded-lg ${p.highlight ? "border-2 border-primary" : "border-border"} relative`}>
              {p.highlight && (
                <span className="absolute -top-3 right-5 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded">
                  Most popular
                </span>
              )}
              <CardContent className="p-6 flex flex-col h-full">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{p.name}</p>
                <p className="font-display text-4xl font-extrabold tracking-tighter mt-2">
                  {p.price}<span className="text-base font-medium text-muted-foreground ml-1">{p.period}</span>
                </p>
                {p.subPrice ? (
                  <p className="text-xs text-muted-foreground mt-1">{p.subPrice}</p>
                ) : null}
                <ul className="mt-5 space-y-1.5 text-sm flex-1">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5 shrink-0" /> {perk}</li>
                  ))}
                </ul>
                {p.action.to ? (
                  <Link to={p.action.to} className="mt-6 block" data-testid={p.testid}>
                    <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">{p.cta}</Button>
                  </Link>
                ) : (
                  <a href={p.action.href} className="mt-6 block" data-testid={p.testid}>
                    <Button variant="outline" className="w-full">{p.cta}</Button>
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6 max-w-2xl mx-auto">
          Paid plans launch alongside our Stripe checkout — until then, free is fully open and paid tiers can be set up by emailing us directly.
        </p>
      </Section>

      {/* Join the Community — newsletter-only signup for people who want the
          podcast/tips but aren't ready to make a full account. Captures more
          emails than the app-signup funnel alone. */}
      <Section id="community" className="py-16 sm:py-24">
        <Card className="border-border shadow-none rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr]">
            <div className="p-8 sm:p-12 bg-primary text-primary-foreground">
              <p className="text-xs uppercase tracking-[0.24em] text-[#CC9C33] font-semibold">Join the community</p>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-3 leading-tight">
                Get the newsletter without signing up for the app.
              </h2>
              <p className="text-sm sm:text-base opacity-90 mt-4 max-w-prose leading-relaxed">
                New Poodle People Podcast episodes, training + grooming tips from working handlers, and seasonal reminders — a couple of emails a month, never spam.
              </p>
              <ul className="text-sm opacity-85 mt-5 space-y-2">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#CC9C33]" /> Zero commitment — one-click unsubscribe or 30-day pause</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#CC9C33]" /> No password or account required</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#CC9C33]" /> Try the app later when you&apos;re ready — free through 2027</li>
              </ul>
            </div>

            <div className="p-8 sm:p-10 bg-card">
              {communityState === "subscribed" ? (
                <div className="text-center py-8" data-testid="community-subscribed">
                  <div className="h-12 w-12 mx-auto rounded-md bg-primary text-primary-foreground flex items-center justify-center mb-4">
                    <Check size={22} />
                  </div>
                  <p className="font-display text-xl font-bold tracking-tight">You&apos;re in.</p>
                  <p className="text-sm text-muted-foreground mt-2 max-w-prose">
                    Check <strong>{community.email}</strong> for a welcome note. See you in the inbox.
                  </p>
                </div>
              ) : communityState === "already" ? (
                <div className="text-center py-8" data-testid="community-already">
                  <div className="h-12 w-12 mx-auto rounded-md bg-secondary text-secondary-foreground flex items-center justify-center mb-4">
                    <Mail size={22} />
                  </div>
                  <p className="font-display text-xl font-bold tracking-tight">Already subscribed.</p>
                  <p className="text-sm text-muted-foreground mt-2 max-w-prose">
                    <strong>{community.email}</strong> is already on the list — thanks for double-checking.
                  </p>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={submitCommunity} data-testid="community-form">
                  <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-semibold">Sign me up</p>
                  <div>
                    <Label htmlFor="community-name">Name <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input
                      id="community-name"
                      value={community.name}
                      onChange={(e) => setCommunity({ ...community, name: e.target.value })}
                      className="mt-1.5"
                      autoComplete="name"
                      data-testid="community-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="community-email">Email</Label>
                    <Input
                      id="community-email"
                      type="email"
                      required
                      value={community.email}
                      onChange={(e) => setCommunity({ ...community, email: e.target.value })}
                      className="mt-1.5"
                      autoComplete="email"
                      data-testid="community-email"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={communityState === "sending"}
                    data-testid="community-submit"
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Mail size={14} className="mr-1.5" />
                    {communityState === "sending" ? "Adding you…" : "Join the community"}
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                    We&apos;ll never share your email. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </div>
        </Card>
      </Section>

      {/* Contact */}
      <Section id="contact" className="py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Get in touch</p>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter mt-2">
              Questions? Ideas? We&apos;d love to hear.
            </h2>
            <p className="text-sm text-muted-foreground mt-4 max-w-prose">
              Share a feature request, ask about Pro, or just say hello. We typically reply within
              two business days.
            </p>
          </div>
          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-7">
              {sent ? (
                <div data-testid="contact-sent" className="text-center py-6">
                  <div className="h-12 w-12 mx-auto rounded-md bg-primary text-primary-foreground flex items-center justify-center mb-4">
                    <Check size={22} />
                  </div>
                  <p className="font-display text-xl font-bold tracking-tight">Message received</p>
                  <p className="text-sm text-muted-foreground mt-2">We&apos;ll be in touch shortly. Thank you.</p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another"
                    className="mt-5 text-sm font-medium underline underline-offset-4 text-primary hover:opacity-80"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={submitContact} data-testid="contact-form">
                  <div>
                    <Label htmlFor="c-name">Name</Label>
                    <Input id="c-name" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" required />
                  </div>
                  <div>
                    <Label htmlFor="c-email">Email</Label>
                    <Input id="c-email" data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" required />
                  </div>
                  <div>
                    <Label htmlFor="c-msg">Message</Label>
                    <Textarea id="c-msg" data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" required />
                  </div>
                  <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                    <Send size={14} className="mr-1.5" /> {sending ? "Sending…" : "Send message"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Instagram follow strip */}
      <Section className="py-12 sm:py-14 bg-secondary/40" data-testid="instagram-strip">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <span className="h-12 w-12 rounded-xl bg-foreground text-background flex items-center justify-center shrink-0">
              <Instagram size={22} />
            </span>
            <p className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
              Follow us on Instagram
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://www.instagram.com/poodlepeoplepodcast/"
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-poodle-people"
              className="inline-flex items-center gap-2 px-4 h-11 rounded-md bg-card border border-border hover:bg-muted text-sm font-medium transition-colors"
            >
              <Instagram size={16} className="text-primary" /> @poodlepeoplepodcast
            </a>
            <a
              href="https://www.instagram.com/yourdogdays/"
              target="_blank"
              rel="noreferrer"
              data-testid="instagram-your-dog-days"
              className="inline-flex items-center gap-2 px-4 h-11 rounded-md bg-card border border-border hover:bg-muted text-sm font-medium transition-colors"
            >
              <Instagram size={16} className="text-primary" /> @yourdogdays
            </a>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-border bg-card mt-10">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <Logo size={64} textClassName="text-xl" />
            <p className="text-xs text-muted-foreground mt-3 max-w-xs">
              The journal, planner, and tracker built for handlers and pet parents who care.
              Unleash the power of your dog!
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">Contact</p>
            <p className="text-sm">
              <a href="mailto:hello@yourdogdays.com" className="underline underline-offset-4">hello@yourdogdays.com</a>
            </p>
            <p className="text-sm text-muted-foreground mt-2">Or use the form above — we read every note.</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">Newsletter</p>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Signing up for an account also subscribes you to occasional Your Dog Days updates &amp;
              newsletters. You can <Link to="/unsubscribe" className="underline underline-offset-4 font-medium" data-testid="footer-unsubscribe-link">unsubscribe anytime</Link>.
            </p>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-6xl mx-auto px-6 sm:px-10 h-14 flex items-center justify-between text-xs text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Your Dog Days. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Link to="/unsubscribe" className="underline underline-offset-4">Unsubscribe</Link>
              <Link to="/login" className="underline underline-offset-4">Sign in</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
